import React from 'react';
import { motion } from 'framer-motion';
import { FiThumbsUp, FiTrendingUp, FiCheckCircle, FiArrowRight, FiUsers, FiMessageSquare, FiBarChart, FiTarget } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { imageConfig } from '../../config/images';

const FacebookMarketingPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-32 overflow-hidden bg-gradient-to-br from-orange-600 via-red-600 to-orange-700">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnptMCAzNmMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLW9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-10"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-6xl font-bold text-white mb-6">
                Facebook Marketing
              </h1>
              <p className="text-2xl text-white/90 mb-8 leading-relaxed">
                Facebook Marketing helps your business reach the right people through targeted ads, meaningful content, and smart audience insights. It's one of the simplest ways to build trust, increase visibility, and drive real results without needing a big marketing team.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center space-x-2 bg-white text-orange-600 px-8 py-4 rounded-full font-semibold hover:bg-orange-500 hover:text-white transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:scale-105"
                >
                  <span>Grow Your Audience</span>
                  <FiArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center space-x-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-orange-600 transition-all duration-300"
                >
                  <span>Get Free Audit</span>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-all duration-500">
                <img 
                  src={imageConfig.services.socialBackground}
                  alt="Facebook Marketing" 
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-orange-900/60 to-transparent"></div>
              </div>
              {/* Floating Stats */}
              <div className="absolute -bottom-8 -left-8 bg-white rounded-2xl shadow-xl p-6 transform hover:scale-110 transition-all duration-300">
                <div className="text-4xl font-bold text-orange-600">10x</div>
                <div className="text-gray-600">Engagement Growth</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What is Facebook Marketing */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">What is Facebook Marketing?</h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Using tools like Facebook Ads Manager, we promote your products or services directly to the people most likely to engage or buy. Whether your goal is leads, sales, or brand awareness, Facebook gives you a strong, reliable platform to grow your business. With billions of active users, Facebook remains the most powerful place to connect with customers and build long-lasting relationships.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <FiUsers className="text-3xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Content Creation</h3>
              <p className="text-gray-600 leading-relaxed">
                High-quality posts, reels, and creative visuals made to match your brand identity and connect with your audience.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <FiMessageSquare className="text-3xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Targeted Facebook Ads</h3>
              <p className="text-gray-600 leading-relaxed">
                Conversion-focused campaigns built and managed through Facebook Ads Manager to reach people who are most likely to take action.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <FiTarget className="text-3xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Community Engagement</h3>
              <p className="text-gray-600 leading-relaxed">
                Replying, interacting, and maintaining a friendly tone to build trust and keep your audience engaged every day.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <FiBarChart className="text-3xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Social Media Marketing Services</h3>
              <p className="text-gray-600 leading-relaxed">
                Full-scale management including posting schedules, content themes, engagement routines, and monthly insights to support long-term growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Our Facebook Marketing Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Strategic approach to build and grow your Facebook presence
            </p>
          </div>

          <div className="max-w-5xl mx-auto space-y-8">
            {[
              {
                step: '01',
                title: 'Audience Research',
                description: 'Analyze your target audience demographics, interests, behaviors, and pain points to create content that resonates.',
                icon: FiUsers
              },
              {
                step: '02',
                title: 'Content Strategy',
                description: 'Develop comprehensive content calendar with mix of educational, entertaining, and promotional posts optimized for engagement.',
                icon: FiMessageSquare
              },
              {
                step: '03',
                title: 'Page Optimization',
                description: 'Optimize your Facebook Business Page with compelling visuals, complete information, and strategic call-to-actions.',
                icon: FiThumbsUp
              },
              {
                step: '04',
                title: 'Content Creation',
                description: 'Create high-quality posts, graphics, videos, and stories that capture attention and drive meaningful engagement.',
                icon: FiMessageSquare
              },
              {
                step: '05',
                title: 'Community Engagement',
                description: 'Active community management including responding to comments, messages, and fostering positive interactions.',
                icon: FiUsers
              },
              {
                step: '06',
                title: 'Performance Analysis',
                description: 'Track key metrics, analyze what content performs best, and continuously optimize strategy based on data.',
                icon: FiBarChart
              }
            ].map((process, index) => (
              <div key={index} className="flex gap-6 items-start group">
                <div className="flex-shrink-0">
                  <div className="bg-gradient-to-br from-orange-500 to-red-600 text-white text-2xl font-bold w-16 h-16 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-all duration-300">
                    {process.step}
                  </div>
                </div>
                <div className="flex-grow bg-gray-50 rounded-xl p-6 group-hover:shadow-xl transition-all duration-300">
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">{process.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{process.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Packages */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Facebook Marketing Packages</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the perfect plan for your business growth
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                name: 'Basic',
                price: '₹15,000',
                duration: '/month',
                popular: false,
                features: [
                  '12 Posts per Month',
                  'Content Calendar',
                  'Basic Graphics',
                  'Page Management',
                  'Monthly Reports',
                  'Email Support'
                ]
              },
              {
                name: 'Professional',
                price: '₹30,000',
                duration: '/month',
                popular: true,
                features: [
                  '20 Posts per Month',
                  'Custom Graphics',
                  'Video Content (2/month)',
                  'Ad Campaign Management',
                  'Community Management',
                  'Bi-Weekly Reports',
                  'Priority Support',
                  'Competitor Analysis'
                ]
              },
              {
                name: 'Enterprise',
                price: 'Custom',
                duration: 'pricing',
                popular: false,
                features: [
                  'Unlimited Posts',
                  'Premium Content',
                  'Video Production',
                  'Multi-Campaign Ads',
                  'Influencer Outreach',
                  'Weekly Reports',
                  'Dedicated Manager',
                  '24/7 Support'
                ]
              }
            ].map((pkg, index) => (
              <div 
                key={index}
                className={`relative bg-white rounded-2xl shadow-lg p-8 transform hover:scale-105 transition-all duration-300 ${
                  pkg.popular ? 'ring-4 ring-orange-500 shadow-2xl' : ''
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                
                <h3 className="text-2xl font-bold text-gray-800 mb-4">{pkg.name}</h3>
                <div className="mb-6">
                  <span className="text-5xl font-bold text-gray-800">{pkg.price}</span>
                  <span className="text-gray-600 ml-2">{pkg.duration}</span>
                </div>
                
                <ul className="space-y-4 mb-8">
                  {pkg.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-3">
                      <FiCheckCircle className="text-orange-600 flex-shrink-0 mt-1" />
                      <span className="text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Link
                  to="/contact"
                  className={`block w-full text-center py-4 rounded-lg font-semibold transition-all duration-300 ${
                    pkg.popular 
                      ? 'bg-orange-600 hover:bg-orange-700 text-white shadow-lg' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                  }`}
                >
                  {pkg.price === 'Custom' ? 'Contact Sales' : 'Get Started'}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Why Choose Us for Facebook Marketing?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              As a trusted Facebook Ads Agency in India, we focus on creating campaigns that feel natural, relatable, and performance-driven — not spammy or forced.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center group">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiTrendingUp className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Running Targeted Ads</h3>
              <p className="text-gray-600 leading-relaxed">
                We run targeted ads that reach people who are truly interested in your products or services, ensuring your budget is spent efficiently.
              </p>
            </div>
            <div className="text-center group">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiMessageSquare className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Relatable Content & Engagement</h3>
              <p className="text-gray-600 leading-relaxed">
                We design relatable content your audience enjoys and build meaningful engagement through comments, replies, and interactions that keep your brand active and visible.
              </p>
            </div>
            <div className="text-center group">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiBarChart className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Data-Driven Performance</h3>
              <p className="text-gray-600 leading-relaxed">
                Using data to lower cost per lead and improve conversions. Our goal is simple: create Facebook marketing that feels human, delivers results, and supports your business growth without wasting your budget.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Common questions about Facebook Marketing
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {[
              {
                question: "Do I really need a Facebook page in 2025?",
                answer: "If your audience is on Facebook, yes. Billions of people still use it daily. The key is not just having a page, but using it strategically with the right mix of organic and paid content."
              },
              {
                question: "What's the difference between boosting a post and running ads?",
                answer: "Boosting is simple but limited. Running full ads through Ads Manager gives you way more targeting, budget control and tracking. We recommend proper ad campaigns for serious results."
              },
              {
                question: "How long before I see results from Facebook marketing?",
                answer: "Organic growth takes 3–6 months. Paid ads can show results within days. We typically see momentum building after the first month once we've tested creatives and audiences."
              },
              {
                question: "Can Facebook marketing work for B2B businesses?",
                answer: "Absolutely. While LinkedIn is popular for B2B, Facebook still has huge reach and lower ad costs. We've helped B2B brands generate quality leads and build authority through Facebook."
              },
              {
                question: "What if I have a small budget?",
                answer: "Start small, test what works, then scale. Even ₹5,000–10,000/month in ad spend can generate real leads when managed well. We focus on ROI, not vanity metrics."
              }
            ].map((faq, index) => (
              <details key={index} className="bg-white rounded-xl shadow-lg p-6 group hover:shadow-2xl transition-all duration-300">
                <summary className="text-xl font-bold text-gray-800 cursor-pointer flex items-center justify-between group-hover:text-orange-600 transition-colors">
                  {faq.question}
                  <FiArrowRight className="transform group-open:rotate-90 transition-transform text-orange-600" />
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-600 to-red-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-5xl font-bold mb-8">Ready to Turn Followers Into Customers?</h2>
          <p className="text-2xl mb-12 text-white/90 max-w-3xl mx-auto">
            Let's create a Facebook presence that doesn't just look good—it actually brings in business. Organic content + smart ads + real engagement.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center space-x-2 bg-white text-orange-600 px-8 py-4 rounded-full font-semibold hover:bg-orange-500 hover:text-white transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:scale-105"
            >
              <span>Get Free Audit</span>
              <FiArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center space-x-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-orange-600 transition-all duration-300"
            >
              <span>Call / WhatsApp Your Team</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FacebookMarketingPage;
